
// Utils
import Vue, { PropType } from 'vue';
import { isFunction, get, chunk } from 'lodash';
import { nowUnixTimestamp } from '@/common/time';

// Player imports
import Api from '@/core/api';
import { Unsubscriber } from '@/core/event-bus';
import { PlayerState } from '@/core/player-state';
import PollsController, {
    PollsConfig,
    PollInterface,
    PollType,
    WordCloudConfig
} from './polls.controller';

// Componenets
import Answer from './answer/Answer.vue';
import WordCloud from '@/word-cloud/WordCloud.vue';
import { WordCloud as WC } from '@/word-cloud/interfaces';

export default Vue.extend({
    name: 'Polls',

    components: {
        Answer,
        WordCloud,
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<PollsConfig>,
            default: () => ({} as PollsConfig)
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        }
    },

    data() {
        return {
            controller: new PollsController(),
            poll: undefined as PollInterface | undefined,
            isVideoElementFullScreen: false,
            unsubscribers: [] as Unsubscriber[],
            countdownStart: 0,
            countdownDuration: undefined as number | undefined,
            nowUnixTimestamp: undefined as number | undefined,
            interval: undefined as number | undefined,
            PollType,
        };
    },

    computed: {
        showResultsOnly(): boolean {
            return get(this, 'config.showResultsOnly');
        },

        resultsColor(): string {
            return get(this, 'config.resultsColor');
        },

        showPoll(): boolean {
            if (this.showResultsOnly) {
                return !!this.poll?.show_results;
            }
            return !!this.poll;
        },

        isEmpty(): boolean {
            return this.isWordCloud
                ? !this.wordCloudWords.length
                : !this.poll?.answers?.length;
        },

        isWordCloud(): boolean {
            return this.poll?.poll_type === PollType.WORD_CLOUD;
        },

        wordCloudConfig(): WordCloudConfig {
            const { colors, font, large } = (this.config as PollsConfig);

            return { colors, font, large };
        },

        questionClass(): string {
            return (this.poll?.poll_type !== PollType.WORD_CLOUD && Number(this.poll?.answers?.length) > 4)
                ? 'line-clamp-2'
                : 'line-clamp-3';
        },

        wordCloudWords(): WC.Word[] {
            return this.poll?.results as WC.Word[] || [];
        },

        timeLeft(): number {
            if (!this.countdownDuration) return -1;
            if (this.countdownStart === 0) return -1;
            if (!this.nowUnixTimestamp) return -1;

            const timeLeft = this.countdownDuration - (this.nowUnixTimestamp - this.countdownStart);
            if (!timeLeft || timeLeft < 0 || Number.isNaN(timeLeft)) return -1;
            else return timeLeft;
        },

        showCountdownTimer(): boolean {
            return this.timeLeft > -1;
        },
        shouldCountdownBlink(): boolean {
            return this.timeLeft <= 10 && this.timeLeft > -1;
        },
        countdownWidth(): number {
            return this.countdownDuration
                ? Math.max(0, Math.min(100, Math.floor(100 * this.timeLeft / this.countdownDuration)))
                : 0;
        },
        countdownColor(): string {
            return get(this.poll, 'countdown_color', '#000000');
        },
    },

    mounted() {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.updateState(partial);
            }
        );

        this.updateState(state);

        this.unsubscribers.push(
            unsubscriber,
            this.api.onMetadataChange(this.onMetadataChange.bind(this)),
            this.api.onClientAction(this.onClientAction.bind(this))
        );
    },

    beforeDestroy() {
        this.clearCountdownInterval();
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }

    },

    methods: {
        updateState(state: Partial<PlayerState>): void {
            this.isVideoElementFullScreen = get(state, 'isVideoElementFullScreen', this.isVideoElementFullScreen);
        },

        async onMetadataChange(payload: unknown) {
            const poll = get(payload, 'value.poll', {});

            this.api.customAction(poll, 'polls');
        },

        onClientAction(payload: unknown, context?: string) {
            if (context !== 'polls') return;

            this.poll = this.parseIncomingPoll(payload as PollInterface);

            this.countdownStart = get(this.poll, 'voting_opened_timestamp', this.poll?.fp_created_timestamp) || 0;
            this.countdownDuration = get(this.poll, 'countdown_duration');
            this.nowUnixTimestamp = nowUnixTimestamp();

            this.clearCountdownInterval();
            if (this.poll && this.countdownDuration) {
                this.interval = setInterval(() => {
                    this.nowUnixTimestamp = nowUnixTimestamp();

                    if (this.timeLeft < 0) {
                        this.clearCountdownInterval();
                    }
                }, 1000);
            }
        },

        parseIncomingPoll(poll?: PollInterface) {
            if (!poll?.poll_id) return undefined;

            const poll_type = poll?.poll_type || PollType.MULTIPLE_CHOICE;
            const answers = this.controller.parseAnswers({ ...poll, poll_type } as PollInterface);

            return {
                ...poll,
                poll_type,
                ...(poll_type === PollType.MULTIPLE_CHOICE ? { rows: chunk(answers, 2) } : { })
            } as PollInterface;
        },

        clearCountdownInterval() {
            clearInterval(this.interval);
            this.interval = undefined;
        }
    }
});
